










import { ScoutingPlace } from "@/models";
import { Component, Ref, Vue } from "vue-property-decorator";
import { Position } from "@/models/Position";
import { currentScoutingStore } from "@/store/typed";
import VGoogleMap from "@/components/VGoogleMap.vue";
import {
  AREA_FILL_COLOR,
  AREA_FILL_COLOR_SELECTED,
  AREA_FILL_COLOR_SELECTED_DETAILS,
  AREA_STROKE_COLOR,
  AREA_STROKE_COLOR_SELECTED,
  AREA_STROKE_COLOR_SELECTED_DETAILS,
  MILAN_LOCATION,
  PIN_ICON_FILL_COLOR,
  PIN_ICON_FILL_COLOR_SELECTED,
  PIN_ICON_STROKE_COLOR,
  PIN_ICON_STROKE_COLOR_SELECTED,
  PIN_ICON_SVG,
  PIN_ICON_ZOOMED_FILL_COLOR,
  PIN_ICON_ZOOMED_STROKE_COLOR,
  POLYGON_FILL_COLOR,
  POLYGON_STROKE_COLOR,
} from "@/utils/Map";
import ScoutingArea from "@/models/ScoutingArea";
import { AnalysisState } from "@/models/Scouting";
import ScoutingGroup from "@/models/ScoutingGroup";

interface MapObject {
  id: string;
  selected: boolean;
  position?: Position;
  path?: Position[];
  multiPolygonMap?: Map<string, any[]>;
  poligonIds?: string[];
}

@Component({
  components: {
    VGoogleMap,
  },
})
export default class ScoutingMap extends Vue {
  @Ref() readonly map!: VGoogleMap;

  private mapObjects: { [id: string]: MapObject } = {};
  private zoomedMapObject: any = null;

  getPinIcon(selected: boolean) {
    return {
      path: PIN_ICON_SVG,
      strokeColor: selected
        ? PIN_ICON_STROKE_COLOR_SELECTED
        : PIN_ICON_STROKE_COLOR,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: selected ? PIN_ICON_FILL_COLOR_SELECTED : PIN_ICON_FILL_COLOR,
      fillOpacity: 1,
      rotation: 0,
      scale: 0.8,
      anchor: new window.google.maps.Point(30, 30),
    };
  }

  getPinIconForZoomDetail() {
    return {
      path: PIN_ICON_SVG,
      strokeColor: PIN_ICON_ZOOMED_STROKE_COLOR,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: PIN_ICON_ZOOMED_FILL_COLOR,
      fillOpacity: 1,
      rotation: 0,
      scale: 0.8,
      anchor: new window.google.maps.Point(30, 30),
    };
  }

  buildMarkerOptions(place: ScoutingPlace) {
    return {
      icon: this.getPinIcon(place.highlighted),
    };
  }

  buildMarkerOptionsForZoomDetail() {
    return {
      icon: this.getPinIconForZoomDetail(),
    };
  }

  buildPolygonOptions() {
    return {
      strokeColor: POLYGON_STROKE_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: POLYGON_FILL_COLOR,
      fillOpacity: 0.35,
    };
  }

  buildAreaOptions(selected = false) {
    return {
      strokeColor: selected ? AREA_STROKE_COLOR_SELECTED : AREA_STROKE_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: selected ? AREA_FILL_COLOR_SELECTED : AREA_FILL_COLOR,
      fillOpacity: 0.5,
    };
  }

  buildAreaOptionsForZoomDetails() {
    return {
      strokeColor: AREA_STROKE_COLOR_SELECTED_DETAILS,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: AREA_FILL_COLOR_SELECTED_DETAILS,
      fillOpacity: 0.5,
    };
  }

  buildGroupOptions(groupType: string) {
    return {
      strokeColor:
        groupType === "area"
          ? AREA_STROKE_COLOR_SELECTED_DETAILS
          : POLYGON_STROKE_COLOR,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor:
        groupType === "area"
          ? AREA_FILL_COLOR_SELECTED_DETAILS
          : POLYGON_FILL_COLOR,
      fillOpacity: 0.5,
    };
  }

  addMarker(place: ScoutingPlace) {
    this.map.addMarker(
      place.id,
      place.position,
      this.buildMarkerOptions(place)
    );
    if (place.isocron?.isocron?.length) {
      this.map.drawPolygon(
        place.id,
        place.isocron.path,
        this.buildPolygonOptions()
      );
    }

    this.mapObjects[place.id] = this.buildMapObjectFromScoutingPlace(place);
  }

  addArea(area: ScoutingArea) {
    let isZoomedMapObject = false;
    if (this.zoomedMapObject) {
      isZoomedMapObject = this.zoomedMapObject.id === area.id;
    }

    let counter = 1;
    this.mapObjects[area.id] = this.buildMapObjectFromScoutingArea(area);
    this.mapObjects[area.id].poligonIds = [];
    const multiPolygonMap: Map<string, any[]> = new Map();
    const polygonIds: string[] = [];
    for (const polygon of area.multiPolygon) {
      const id = `${area.id}#${counter}`;
      polygonIds.push(id);
      this.map.drawPolygon(
        id,
        polygon,
        isZoomedMapObject
          ? this.buildAreaOptionsForZoomDetails()
          : this.buildAreaOptions(area.highlighted)
      );
      counter++;
      multiPolygonMap.set(id, polygon);
    }
    this.mapObjects[area.id].poligonIds = polygonIds;
    this.mapObjects[area.id].multiPolygonMap = multiPolygonMap;
  }

  addGroup(group: ScoutingGroup) {
    let counter = 1;
    this.mapObjects[group.id] = this.buildMapObjectFromScoutingGroup(group);
    this.mapObjects[group.id].poligonIds = [];
    const multiPolygonMap: Map<string, any[]> = new Map();
    const polygonIds: string[] = [];
    for (const polygon of group.multiPolygon) {
      const id = `${group.id}#${counter}`;
      polygonIds.push(id);
      this.map.drawPolygon(
        id,
        polygon,
        this.buildGroupOptions(group.groupType)
      );
      counter++;
      multiPolygonMap.set(id, polygon);
    }
    this.mapObjects[group.id].poligonIds = polygonIds;
    this.mapObjects[group.id].multiPolygonMap = multiPolygonMap;

    const scoutingPlaces = group.scoutingPlaces;

    if (scoutingPlaces.length) {
      scoutingPlaces.forEach((scoutingPlace) => {
        scoutingPlace.highlighted = true;
        this.map.addMarker(
          scoutingPlace.id,
          scoutingPlace.position,
          this.buildMarkerOptions(scoutingPlace)
        );
      });
    }
  }

  buildMapObjectFromScoutingPlace(place: ScoutingPlace) {
    return { id: place.id, selected: place.highlighted };
  }

  buildMapObjectFromScoutingArea(area: ScoutingArea) {
    return { id: area.id, selected: area.highlighted };
  }

  buildMapObjectFromScoutingGroup(group: ScoutingGroup) {
    return { id: group.id, selected: true };
  }

  centerOnPlaces(scoutingPlaces: ScoutingPlace[]) {
    const path: Position[] = [];
    for (const place of scoutingPlaces) {
      if (place.isIsocronReady) {
        path.push(...place.isocron.path);
      } else {
        path.push(place.position);
      }
    }

    if (!path.length) {
      return;
    }

    this.map.centerOnBounds(path);
  }

  centerOnPlace(scoutingPlace: ScoutingPlace) {
    this.zoomedMapObject = scoutingPlace;
    this.map.updateMarker(
      scoutingPlace.id,
      scoutingPlace.position,
      this.buildMarkerOptionsForZoomDetail()
    );
    if (scoutingPlace.isIsocronReady) {
      this.map.centerOnBounds(scoutingPlace.isocron?.path, 13);
    } else {
      this.map.centerOnPosition(scoutingPlace.position, 13);
    }
  }

  centerOnArea(scoutingArea: ScoutingArea) {
    if (!scoutingArea.multiPolygon || scoutingArea.multiPolygon.length === 0) {
      return;
    }

    this.zoomedMapObject = scoutingArea;
    if (
      this.mapObjects[scoutingArea.id] &&
      this.mapObjects[scoutingArea.id].poligonIds
    ) {
      const polygonIds = this.mapObjects[scoutingArea.id].poligonIds;
      if (polygonIds) {
        for (const polygonId of polygonIds) {
          this.map.updatePolygon(
            polygonId,
            undefined,
            this.buildAreaOptionsForZoomDetails()
          );
        }
      }
    }

    const path: Position[] = [];
    for (const polygon of scoutingArea.multiPolygon) {
      for (const subpolygon of polygon) {
        for (const positions of subpolygon) {
          path.push(positions);
        }
      }
    }
    this.map.centerOnBounds(path);
  }

  centerOnGroupArea(group: ScoutingGroup) {
    if (!group.multiPolygon || group.multiPolygon.length === 0) {
      return;
    }

    this.zoomedMapObject = group;
    const path: Position[] = [];
    for (const polygon of group.multiPolygon) {
      for (const subpolygon of polygon) {
        for (const positions of subpolygon) {
          path.push(positions);
        }
      }
    }
    this.map.centerOnBounds(path);
  }

  get searchScoutingPlaces() {
    return currentScoutingStore.searchScoutingPlaces;
  }

  get scoutingPlaces() {
    return [
      ...currentScoutingStore.scoutingPlaces,
      ...(currentScoutingStore.scoutingAnalysis?.scouting.scoutingPlaces || []),
    ];
  }

  get searchScoutingAreas() {
    return currentScoutingStore.searchScoutingAreas;
  }

  get scoutingAreas() {
    return [
      ...currentScoutingStore.scoutingAreas,
      ...(currentScoutingStore.scoutingAnalysis?.scouting.scoutingAreas || []),
    ];
  }

  centerOnMarkers() {
    this.restoreZoomedMapObject();

    if (this.searchScoutingPlaces.length) {
      this.centerOnPlaces(this.searchScoutingPlaces);
    } else {
      if (this.scoutingPlaces.length) {
        this.centerOnPlaces(this.scoutingPlaces);
      }
    }
  }

  centerOnAreas() {
    this.restoreZoomedMapObject();
    const areas = [];
    if (this.searchScoutingAreas.length) {
      areas.push(...this.searchScoutingAreas);
    } else {
      if (this.scoutingAreas.length) {
        areas.push(...this.scoutingAreas);
      }
    }

    const path: Position[] = [];
    for (const area of areas) {
      for (const polygon of area.multiPolygon) {
        for (const subpolygon of polygon) {
          for (const positions of subpolygon) {
            path.push(positions);
          }
        }
      }
    }
    this.map.centerOnBounds(path);
  }

  centerOnSelectedAreas(areas: ScoutingArea[]) {
    const path: Position[] = [];
    for (const area of areas) {
      for (const polygon of area.multiPolygon) {
        for (const subpolygon of polygon) {
          for (const positions of subpolygon) {
            path.push(positions);
          }
        }
      }
    }

    if (!path.length) {
      return;
    }

    this.map.centerOnBounds(path);
  }

  centerAllScoutingObjects() {
    this.restoreZoomedMapObject();

    let scoutingPlaces: ScoutingPlace[] = [];
    if (this.searchScoutingPlaces.length)
      scoutingPlaces = this.searchScoutingPlaces;
    else scoutingPlaces = this.scoutingPlaces;

    let scoutingAreas: ScoutingArea[] = [];
    if (this.searchScoutingAreas.length)
      scoutingAreas = this.searchScoutingAreas;
    else scoutingAreas = this.scoutingAreas;

    const path: Position[] = [];
    for (const place of scoutingPlaces) {
      path.push(place.position);
      if (place.isIsocronReady) path.push(...place.isocron.path);
    }

    for (const area of scoutingAreas) {
      for (const polygon of area.multiPolygon) {
        for (const subpolygon of polygon) {
          for (const positions of subpolygon) {
            path.push(positions);
          }
        }
      }
    }

    if (path.length === 0) {
      this.map.centerOnPosition(MILAN_LOCATION);
    } else {
      this.map.centerOnBounds(path);
    }

    this.map.refreshMap();
  }

  updateMarker(place: ScoutingPlace) {
    this.map.updateMarker(
      place.id,
      place.position,
      this.buildMarkerOptions(place)
    );
    if (place.isocron?.isocron?.length && place.isocron?.isCompleted) {
      this.updatePlacePolygon(place);
    }

    this.mapObjects[place.id] = this.buildMapObjectFromScoutingPlace(place);
  }

  updateArea(area: ScoutingArea) {
    let counter = 1;
    for (const polygon of area.multiPolygon) {
      const id = `${area.id}#${counter}`;
      this.map.updatePolygon(
        id,
        polygon.positions,
        this.buildAreaOptions(area.highlighted)
      );
      counter++;
    }
    this.mapObjects[area.id].selected = area.highlighted;
  }

  addPlacePolygon(place: ScoutingPlace) {
    this.addMarker(place);
    if (place.isocron?.isocron?.length && place.isocron?.isCompleted) {
      this.map.drawPolygon(
        place.id,
        place.isocron.path,
        this.buildPolygonOptions()
      );
    }
  }

  updatePlacePolygon(place: ScoutingPlace) {
    this.map.updatePolygon(place.id, place.isocron?.path);
  }

  removePlacePolygon(id: string) {
    this.map.removeMarker(id);
  }

  clearMarkers() {
    if (!this.map) {
      return;
    }

    this.map.clearMarkers();
  }

  removeMarker(id: string) {
    this.map.removeMarker(id);
  }

  removeArea(id: string) {
    if (this.mapObjects[id]) {
      const listPolygons = this.mapObjects[id].poligonIds;
      if (listPolygons) {
        for (const polygonId of listPolygons) {
          this.map.removePolygon(polygonId);
        }
      }
    }
  }

  removeGroup(id: string) {
    if (this.mapObjects[id]) {
      const listPolygons = this.mapObjects[id].poligonIds;
      if (listPolygons) {
        for (const polygonId of listPolygons) {
          this.map.removePolygon(polygonId);
        }
      }
    }

    this.clearMarkers();
  }

  async onMarkerClick(markerId: string) {
    if (
      this.$route.name === "scouting-poi-search" ||
      this.$route.name === "create_group"
    ) {
      this.mapObjects[markerId].selected = !this.mapObjects[markerId].selected;
      const scoutingPlace = new ScoutingPlace();
      scoutingPlace.id = markerId;
      scoutingPlace.highlighted = this.mapObjects[markerId].selected;

      this.updateMarker(scoutingPlace);
      currentScoutingStore.updateSelectionScoutingPlace(scoutingPlace);
    }

    if (
      this.$route.name === "scouting-poi" ||
      this.$route.name === "scouting-isocron"
    ) {
      const scoutingPlace = currentScoutingStore.scoutingPlaces.find(
        (o) => o.id === markerId
      );
      if (scoutingPlace) {
        this.centerOnPlace(scoutingPlace);

        this.$router.push({
          name: "scouting-poi-show",
          params: { id: scoutingPlace.id },
        });
      }
    }

    if (this.$route.name === "company_analysis") {
      if (
        currentScoutingStore.scoutingAnalysis?.status === AnalysisState.National
      ) {
        await currentScoutingStore.changeScoutingAnalysisState(
          AnalysisState.POI
        );
        const analysis = currentScoutingStore.scoutingAnalysis;
        if (analysis) {
          const selectedScoutingPlace = analysis.scouting.scoutingPlaces.find(
            (o) => o.id === markerId
          );
          if (selectedScoutingPlace)
            await currentScoutingStore.changeScoutingAnalysisCurrentPlace(
              selectedScoutingPlace
            );
        }
      }
    }
  }

  restoreZoomedMapObject() {
    if (this.zoomedMapObject) {
      if (this.zoomedMapObject instanceof ScoutingPlace) {
        const scoutingPlace = this.zoomedMapObject as ScoutingPlace;
        this.map.updateMarker(
          scoutingPlace.id,
          scoutingPlace.position,
          this.buildMarkerOptions(scoutingPlace)
        );
      }
      if (this.zoomedMapObject instanceof ScoutingArea) {
        const scoutingArea = this.zoomedMapObject as ScoutingArea;
        if (
          this.mapObjects[scoutingArea.id] &&
          this.mapObjects[scoutingArea.id].poligonIds
        ) {
          const polygonIds = this.mapObjects[scoutingArea.id].poligonIds;
          if (polygonIds) {
            for (const polygonId of polygonIds) {
              this.map.updatePolygon(
                polygonId,
                undefined,
                this.buildAreaOptions(true)
              );
            }
          }
        }
      }

      this.zoomedMapObject = null;
    }
  }

  async onPolygonClick(polygonId: string) {
    if (this.$route.name === "scouting-area-search") {
      const parentId = polygonId.substring(0, polygonId.indexOf("#"));
      this.mapObjects[parentId].selected = !this.mapObjects[parentId].selected;
      const scoutingArea = currentScoutingStore.searchScoutingAreas.find(
        (o) => o.id === parentId
      );
      if (scoutingArea) {
        scoutingArea.highlighted = this.mapObjects[parentId].selected;
        this.updateArea(scoutingArea);
        currentScoutingStore.updateSelectionScoutingArea(scoutingArea);
      }
    }
    if (this.$route.name === "scouting-poi") {
      const parentId = polygonId.substring(0, polygonId.indexOf("#"));
      const scoutingArea = currentScoutingStore.scoutingAreas.find(
        (o) => o.id === parentId
      );
      if (scoutingArea) {
        this.centerOnArea(scoutingArea);
        this.$router.push({
          name: "scouting-area-show",
          params: { id: scoutingArea.id },
        });
      }
    }

    if (this.$route.name === "company_analysis") {
      if (
        currentScoutingStore.scoutingAnalysis?.status === AnalysisState.National
      ) {
        await currentScoutingStore.changeScoutingAnalysisState(
          AnalysisState.Area
        );
        const analysis = currentScoutingStore.scoutingAnalysis;
        if (analysis) {
          const areaId = polygonId.substring(0, polygonId.indexOf("#"));
          const selectedScoutingArea = analysis.scouting.scoutingAreas.find(
            (o) => o.id === areaId
          );
          if (selectedScoutingArea)
            await currentScoutingStore.changeScoutingAnalysisCurrentArea(
              selectedScoutingArea
            );
        }
      }
    }
  }
}
